import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import TooltipEllipsis from '@deltek/specification-client/core/Feedbacks/TooltipEllipsis'
import { UserWithProjectRole } from '@deltek/specification-client/core/Models/firm'
import { FormPropsObject } from '@deltek/specification-client/core/Models/formProps'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import {
    hasError,
    isFormValueDirty,
    StateSetStateProps,
    updateFormState,
    validateFormState,
} from '@deltek/specification-client/core/Utilities/formUtils'
import {
    getFullAddress,
    getNameInitials,
    suffixDimension,
} from '@deltek/specification-client/core/Utilities/stringUtils'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import SpecImage from '@deltek/specification-client/core/Widgets/SpecImage'
import { Divider, FormControl, TextField } from '@material-ui/core'
import Avatar from '@material-ui/core/Avatar'
import Popper, { PopperProps } from '@material-ui/core/Popper'
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles'
import Typography from '@material-ui/core/Typography'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import Autocomplete from '@material-ui/lab/Autocomplete'
import clsx from 'clsx'
import { ExecutionResult } from 'graphql'
import { Assignment } from 'Models/project'
import React, { useEffect, useState } from 'react'

const useContentStyles = makeStyles((theme: Theme) => ({
    teamMemberContentInput: {
        '& label.Mui-focused': {
            color: 'white',
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: 'white',
        },
        '& .MuiOutlinedInput-root': {
            padding: 0,
            '&.MuiAutocomplete-inputRoot': {
                padding: 1,
                paddingRight: 30,

                '& .MuiAutocomplete-endAdornment': {
                    width: 30,
                    height: 28,
                    backgroundColor: '#fff',
                    right: 0,
                    borderTopRightRadius: 4,
                    borderBottomRightRadius: 4,
                    '& .MuiIconButton-root': {
                        width: 30,
                        height: 30,
                    },
                },
            },
            '& .MuiOutlinedInput-input': {
                backgroundColor: '#fff',
                fontSize: pxToRem(14),
                padding: 5,
            },
            '& fieldset': {
                borderColor: 'white',
            },
            '&:hover fieldset': {
                borderColor: 'white',
            },
            '&.Mui-focused fieldset': {
                borderColor: 'white',
            },
            '& textarea': {
                borderRadius: 4,
            },
        },
    },
    flexible: {
        flex: 1,
    },
    editModeBottomPadding: {
        paddingBottom: theme.spacing(1.25),
    },
    nonEditModeBottomPadding: {
        paddingBottom: theme.spacing(0.25),
    },
    buttonColumn: {
        width: '50%',
        '& button': {
            width: theme.spacing(11),
            textTransform: 'capitalize',
            height: theme.spacing(4.25),
        },
    },
    cancelButtonColumn: {
        '& button:hover': {
            borderColor: theme.palette.secondary.main,
        },
    },
    saveButtonColumn: {
        '& button:disabled': {
            backgroundColor: theme.palette.secondary.main,
            opacity: '25%',
            color: '#fff',
        },
    },

    firstButtonColumn: {
        '& .roundButtonContainer': {
            float: 'right',
            paddingRight: theme.spacing(1.25),
        },
    },
    buttonRow: {
        height: theme.spacing(3.375),
    },
    buttonText: {
        fontSize: pxToRem(14),
    },
    editButton: {
        fontSize: pxToRem(13),
    },
    RowHeading: {
        width: 79,
    },
    editModePadding: {
        display: 'flex',
        flexDirection: 'column',
        paddingTop: 5,
    },
    pointer: {
        cursor: 'pointer',
    },
    dividerRow: {
        paddingBottom: theme.spacing(1),
        paddingTop: theme.spacing(1),
    },
    divider: {
        margin: 0,
        marginLeft: -17,
        marginRight: -10,
        border: '1px solid rgba(255, 255, 255, 0.25);',
    },
    valueColumn: {
        paddingLeft: theme.spacing(1.5),
        width: 218,
    },
    italicValueColumn: {
        fontStyle: 'italic',
    },
    iconColumn: {
        width: theme.spacing(1.75),
    },
    headingColumn: {
        width: 'fit-content',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    headingText: {
        borderBottom: `3px solid ${theme.palette.common.white}`,
        lineHeight: '24px',
        fontSize: pxToRem(16),
        fontWeight: 500,
    },
    descriptionStyle: {
        fontSize: pxToRem(14),
        fontWeight: 'normal',
        lineHeight: '1.43',
    },
    infoBlock: {
        marginTop: theme.spacing(2),
        marginBottom: 0,
        width: 312,
        wordBreak: 'break-word',
        paddingLeft: theme.spacing(1.25),
    },
    infoLabel: {
        '&:after': {
            content: '":"',
        },
    },
    changePicture: {
        height: 50,
        width: 50,
        backgroundColor: theme.palette.primary.main,
        margin: 0,
        marginRight: theme.spacing(0.75),
        float: 'left',
    },
    profileImageHeightWidth: {
        width: 50,
        height: 50,
    },
    profileImageImg: {
        objectFit: 'scale-down',
        objectPosition: 'center',
        borderRadius: '50%',
        float: 'left',
        marginRight: theme.spacing(0.75),
        border: '1px solid #FBB41F',
    },
    backIcon: {
        cursor: 'pointer',
    },
}))

export interface ProjectTeamMemberPopoutContentProps {
    userWithProjectRole: UserWithProjectRole
    assignment: Partial<Assignment> | null | undefined
    setSelectedAssignment?: (val: Partial<Assignment> | null) => void
    updateAssignment?: (
        projectRole: string,
        description: string,
        userid: string
    ) => Promise<
        ExecutionResult<{
            updateProjectTeamAssignment: Partial<Assignment> | null
        }>
    >
    updateAssignmentLoading: boolean
    handlePopoutClose: VoidFunction
    showBackButton?: boolean
    getRoles: (disciplineName: string) => Record<'name' | 'value', string>[]
    canEdit?: boolean
    setContentInEditMode?: (editMode: boolean) => void
}

const ProjectTeamMemberPopoutContent: React.FC<ProjectTeamMemberPopoutContentProps> = React.memo(
    ({
        userWithProjectRole,
        assignment,
        setSelectedAssignment,
        updateAssignment,
        updateAssignmentLoading,
        handlePopoutClose,
        getRoles,
        showBackButton = false,
        canEdit = true,
        setContentInEditMode,
    }) => {
        const classes = useContentStyles()
        const theme = useTheme()

        const { user, projectRole } = userWithProjectRole
        const roles = getRoles(user?.profileInfo?.professionalDiscipline!)

        const [editMode, setEditMode] = useState(false)
        const [formState, setFormState] = useState<FormPropsObject>(
            {} as FormPropsObject
        )
        const formStateRef = React.useRef<FormPropsObject>(formState)

        const [isFormDirty, setIsFormDirty] = React.useState<boolean>(false)
        const [baseFormState, setBaseFormState] = React.useState<
            FormPropsObject
        >({} as FormPropsObject)

        const [isPageValidOnSave, setIsPageValidOnSave] = React.useState<
            boolean
        >(false)

        const userInitials = getNameInitials(
            user.profileInfo?.firstName?.toUpperCase(),
            user.profileInfo?.lastName?.toUpperCase()
        )
        const updateUserFormState = updateFormState({
            state: formState,
            setState: setFormState,
        } as StateSetStateProps)

        const handleBackClick = React.useCallback(() => {
            setEditMode(false)
            setSelectedAssignment?.(null)
            if (setContentInEditMode) {
                setContentInEditMode(false)
            }
        }, [setEditMode, setContentInEditMode, setSelectedAssignment])
        const handleEditClick = React.useCallback(() => {
            setEditMode(true)
            if (setContentInEditMode) {
                setContentInEditMode(true)
            }
        }, [setEditMode, setContentInEditMode])

        const onBtnCancel = () => {
            const formObject = JSON.stringify(createFormObj())
            setFormState(JSON.parse(formObject))
            setBaseFormState(JSON.parse(formObject))
            setEditMode(false)
            setIsFormDirty(false)
            handlePopoutClose?.()
        }

        const onBtnSave = () => {
            const isPageValid = validateFormState({
                state: formStateRef.current,
                setState: setFormState as React.Dispatch<
                    React.SetStateAction<FormPropsObject>
                >,
            })
            if (isPageValid) {
                setIsPageValidOnSave(true)
                updateAssignment!(
                    formState.projectRole.value!,
                    formState.description.value!,
                    user.id!
                ).then(() => {
                    setEditMode(false)
                    setBaseFormState(JSON.parse(JSON.stringify(formState)))
                    setFormState(JSON.parse(JSON.stringify(formState)))
                    setIsFormDirty(false)
                    formStateRef.current = formState
                    handlePopoutClose?.()
                })
            }
        }
        const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            updateUserFormState({
                name: event.currentTarget.name,
                value: event.currentTarget.value,
            })
        }

        const createFormObj = React.useCallback(
            (): FormPropsObject => ({
                description: {
                    value: assignment?.description || '',
                    required: false,
                },
                projectRole: {
                    value: projectRole,
                    required: true,
                    maxLength: 100,
                },
            }),
            [assignment, projectRole]
        )

        useEffect(() => {
            if (updateAssignmentLoading) return
            if (editMode) return
            if (user) {
                const formObject = JSON.stringify(createFormObj())
                setFormState(JSON.parse(formObject))
                setBaseFormState(JSON.parse(formObject))
                formStateRef.current = JSON.parse(formObject)
            }
        }, [user, updateAssignmentLoading, editMode, createFormObj])

        React.useEffect(() => {
            if (isPageValidOnSave) {
                setIsFormDirty(false)
                return
            }

            const isCurrentlyDirty = isFormValueDirty({
                prevFormState: baseFormState,
                currentFormState: formState,
            })
            if (isFormDirty !== isCurrentlyDirty) {
                setIsFormDirty(isCurrentlyDirty)
            }
            formStateRef.current = formState
        }, [formState, isFormDirty, isPageValidOnSave, baseFormState])

        if (typeof userWithProjectRole === 'undefined') return null
        return (
            <>
                <GridContainer data-testid="projectTeamMemberPopoutContent">
                    <GridItem className={classes.flexible}>
                        <GridContainer direction="row">
                            {showBackButton && (
                                <GridItem
                                    className={clsx(
                                        classes.headingColumn,
                                        classes.iconColumn
                                    )}
                                >
                                    <ArrowBackIosIcon
                                        fontSize="small"
                                        className={classes.backIcon}
                                        onClick={handleBackClick}
                                    />
                                </GridItem>
                            )}
                            <GridItem>
                                {!!user.profileInfo?.imageUrl ? (
                                    <SpecImage
                                        alt="profile image"
                                        src={suffixDimension({
                                            filename: user.profileInfo
                                                ?.imageUrl!,
                                            dimension: {
                                                height: 150,
                                                width: 150,
                                            },
                                        })}
                                        additionalClassNames={clsx(
                                            classes.profileImageImg,
                                            classes.profileImageHeightWidth
                                        )}
                                        data-testid="profileImage"
                                        height="50"
                                        width="50"
                                    />
                                ) : (
                                    <Avatar className={classes.changePicture}>
                                        {userInitials}
                                    </Avatar>
                                )}
                            </GridItem>
                            <GridItem className={classes.headingColumn}>
                                <div className={classes.headingText}>
                                    <TooltipEllipsis
                                        text={`${user.profileInfo?.firstName} ${user.profileInfo?.lastName}`}
                                        width={200}
                                    >
                                        {`${user.profileInfo?.firstName} ${user.profileInfo?.lastName}`}
                                    </TooltipEllipsis>
                                </div>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    {canEdit && !editMode && (
                        <GridItem
                            className={clsx(
                                classes.pointer,
                                classes.headingColumn
                            )}
                        >
                            <Typography
                                variant="body2"
                                onClick={handleEditClick}
                                data-testid="editButton"
                                className={classes.editButton}
                            >
                                Edit
                            </Typography>
                        </GridItem>
                    )}
                </GridContainer>

                <GridContainer direction="column" className={classes.infoBlock}>
                    <GridItem>
                        <GridContainer>
                            <GridItem className={classes.RowHeading}>
                                <Typography
                                    className={clsx(
                                        classes.infoLabel,
                                        'projectGroupLabel'
                                    )}
                                    variant="body2"
                                    component="span"
                                >
                                    Discipline
                                </Typography>
                            </GridItem>
                            <GridItem
                                className={clsx(
                                    classes.valueColumn,
                                    classes.nonEditModeBottomPadding
                                )}
                            >
                                <Typography variant="body2" component="span">
                                    {user?.profileInfo?.professionalDiscipline}
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem>
                        <GridContainer>
                            <GridItem
                                className={clsx(
                                    classes.RowHeading,
                                    editMode && classes.editModePadding
                                )}
                            >
                                <Typography
                                    className={clsx(
                                        classes.infoLabel,
                                        'projectGroupLabel'
                                    )}
                                    variant="body2"
                                    component="span"
                                >
                                    Project Role
                                </Typography>
                            </GridItem>
                            {!editMode && (
                                <GridItem
                                    className={clsx(
                                        classes.valueColumn,
                                        classes.nonEditModeBottomPadding
                                    )}
                                >
                                    <Typography
                                        variant="body2"
                                        component="span"
                                    >
                                        {projectRole}
                                    </Typography>
                                </GridItem>
                            )}
                            {editMode && !!formState.projectRole && (
                                <GridItem
                                    className={clsx(
                                        classes.valueColumn,
                                        classes.editModeBottomPadding
                                    )}
                                >
                                    <FormControl fullWidth variant="outlined">
                                        <Autocomplete
                                            size="small"
                                            PopperComponent={(
                                                props: PopperProps
                                            ) => (
                                                <Popper
                                                    {...props}
                                                    placement="bottom"
                                                    disablePortal
                                                />
                                            )}
                                            options={roles ?? []}
                                            disabled={false}
                                            disableClearable
                                            getOptionLabel={(
                                                option: Record<
                                                    'name' | 'value',
                                                    string
                                                >
                                            ) => option.name}
                                            getOptionSelected={(
                                                option,
                                                value
                                            ) => option.value === value.value}
                                            onChange={(
                                                _: React.ChangeEvent<{}>,
                                                role: Record<
                                                    'name' | 'value',
                                                    string
                                                > | null
                                            ) => {
                                                updateUserFormState({
                                                    name: 'projectRole',
                                                    value: role?.value ?? '',
                                                })
                                            }}
                                            value={roles?.find(
                                                (role) =>
                                                    role.value ===
                                                    formState.projectRole.value
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    disabled={false}
                                                    name="projectRole"
                                                    variant="outlined"
                                                    size="small"
                                                    value={
                                                        formState.projectRole
                                                            .value
                                                    }
                                                    required={
                                                        formState.projectRole
                                                            .required
                                                    }
                                                    error={hasError(
                                                        formState.projectRole
                                                    )}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    className={
                                                        classes.teamMemberContentInput
                                                    }
                                                    InputProps={{
                                                        ...params.InputProps,
                                                    }}
                                                    inputProps={{
                                                        ...params.inputProps,
                                                        'aria-autocomplete':
                                                            'none',
                                                        autoComplete:
                                                            'chrome-off',
                                                    }}
                                                />
                                            )}
                                        />
                                    </FormControl>
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>

                    <GridItem>
                        <GridContainer>
                            <GridItem
                                className={clsx(
                                    classes.RowHeading,
                                    editMode && classes.editModePadding
                                )}
                            >
                                <Typography
                                    className={clsx(
                                        classes.infoLabel,
                                        'projectGroupLabel'
                                    )}
                                    variant="body2"
                                    component="span"
                                >
                                    Description
                                </Typography>
                            </GridItem>
                            {!editMode && (
                                <GridItem
                                    className={clsx(
                                        classes.valueColumn,
                                        classes.descriptionStyle,
                                        !assignment?.description &&
                                            classes.italicValueColumn
                                    )}
                                >
                                    <TooltipEllipsis
                                        text={
                                            !!assignment?.description
                                                ? assignment?.description
                                                : 'Add Description'
                                        }
                                        width={1000}
                                    >
                                        {!!assignment?.description
                                            ? assignment?.description
                                            : 'Add Description'}
                                    </TooltipEllipsis>
                                </GridItem>
                            )}
                            {editMode && !!formState.description && (
                                <GridItem className={classes.valueColumn}>
                                    <FormControl
                                        data-testid="dvDescription"
                                        fullWidth
                                        variant="outlined"
                                    >
                                        <TextField
                                            multiline
                                            rows={3}
                                            disabled={false}
                                            name="description"
                                            variant="outlined"
                                            size="small"
                                            value={formState.description.value}
                                            required={
                                                formState.description.required
                                            }
                                            error={hasError(
                                                formState.description
                                            )}
                                            onChange={handleChange}
                                            fullWidth
                                            className={
                                                classes.teamMemberContentInput
                                            }
                                            inputProps={{
                                                'aria-autocomplete': 'none',
                                                autoComplete: 'chrome-off',
                                            }}
                                        />
                                    </FormControl>
                                </GridItem>
                            )}
                        </GridContainer>
                    </GridItem>
                    <GridItem className={classes.dividerRow}>
                        <Divider
                            variant="fullWidth"
                            className={classes.divider}
                        />
                    </GridItem>
                    {!!getFullAddress(user.profileInfo?.address) && (
                        <>
                            <GridItem>
                                <GridContainer>
                                    <GridItem className={classes.RowHeading}>
                                        <Typography
                                            className={clsx(
                                                classes.infoLabel,
                                                'projectGroupLabel'
                                            )}
                                            variant="body2"
                                            component="span"
                                        >
                                            Address
                                        </Typography>
                                    </GridItem>
                                    <GridItem className={classes.valueColumn}>
                                        <Typography
                                            variant="body2"
                                            component="span"
                                        >
                                            {getFullAddress(
                                                user.profileInfo?.address
                                            )}
                                        </Typography>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                            <GridItem className={classes.dividerRow}>
                                <Divider
                                    variant="fullWidth"
                                    className={classes.divider}
                                />
                            </GridItem>
                        </>
                    )}
                    <GridItem>
                        <GridContainer>
                            <GridItem className={classes.RowHeading}>
                                <Typography
                                    className={clsx(
                                        classes.infoLabel,
                                        'projectGroupLabel'
                                    )}
                                    variant="body2"
                                    component="span"
                                >
                                    Email
                                </Typography>
                            </GridItem>
                            <GridItem>
                                <Typography
                                    variant="body2"
                                    component="div"
                                    className={classes.valueColumn}
                                >
                                    <TooltipEllipsis
                                        text={user.email}
                                        width={200}
                                    >
                                        {user.email}
                                    </TooltipEllipsis>
                                </Typography>
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    {user.profileInfo?.phone && (
                        <GridItem>
                            <GridContainer>
                                <GridItem className={classes.RowHeading}>
                                    <Typography
                                        className={clsx(
                                            classes.infoLabel,
                                            'projectGroupLabel'
                                        )}
                                        variant="body2"
                                        component="span"
                                    >
                                        Phone
                                    </Typography>
                                </GridItem>
                                <GridItem className={classes.valueColumn}>
                                    <Typography
                                        variant="body2"
                                        component="span"
                                    >
                                        {user.profileInfo?.phone}
                                    </Typography>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    )}
                    {editMode && (
                        <>
                            <GridItem className={classes.dividerRow}>
                                <Divider
                                    variant="fullWidth"
                                    className={classes.divider}
                                />
                            </GridItem>
                            <GridItem className={classes.buttonRow}>
                                <GridContainer>
                                    <GridItem
                                        className={clsx(
                                            classes.firstButtonColumn,
                                            classes.buttonColumn,
                                            classes.cancelButtonColumn
                                        )}
                                    >
                                        <RoundButton
                                            size="small"
                                            hasShadow={false}
                                            color="#FFF"
                                            borderColor="#fff"
                                            backgroundColor="#5E5F61"
                                            data-testid="cancelTeamAssignment"
                                            hoverColor="#E69C24"
                                            onClick={onBtnCancel}
                                        >
                                            <Typography
                                                className={classes.buttonText}
                                            >
                                                CANCEL
                                            </Typography>
                                        </RoundButton>
                                    </GridItem>
                                    <GridItem
                                        className={clsx(
                                            classes.buttonColumn,
                                            classes.saveButtonColumn
                                        )}
                                    >
                                        <RoundButton
                                            size="small"
                                            hasShadow={false}
                                            color="#fff"
                                            borderColor={
                                                theme.palette.secondary.main
                                            }
                                            backgroundColor={
                                                theme.palette.secondary.main
                                            }
                                            data-testid="saveTeamAssignment"
                                            hoverColor="#E69C24"
                                            onClick={onBtnSave}
                                            loading={updateAssignmentLoading}
                                            disabled={!isFormDirty}
                                        >
                                            <Typography
                                                className={classes.buttonText}
                                            >
                                                SAVE
                                            </Typography>
                                        </RoundButton>
                                    </GridItem>
                                </GridContainer>
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            </>
        )
    }
)

export default ProjectTeamMemberPopoutContent
